import React, { useState, useEffect } from 'react';
import api, { getImageUrl } from '../services/api';
import './UserProfile.css';

interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
  role: string;
  is_active: boolean;
  profile_picture?: string | null;
  date_of_birth?: string;
  address?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relation?: string;
  occupation?: string;
  national_id?: string;
  passport_number?: string;
  blood_group?: string;
  notes?: string;
  flat_no?: string;
  building?: {
    id: number;
    name: string;
    address: string;
  };
  ownerships?: Array<{
    id: number;
    flat_number: string;
    ownership_type: string;
    ownership_percentage: number;
    building: {
      id: number;
      name: string;
    };
    flat: {
      id: number;
      flat_type: string;
    };
  }>;
}

const UserProfile: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'personal' | 'contact' | 'documents' | 'password'>('personal');
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    date_of_birth: '',
    address: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: '',
    occupation: '',
    national_id: '',
    passport_number: '',
    blood_group: '',
    notes: '',
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/profile');
      if (response.data.success) {
        setUser(response.data.data);
        setFormData({
          name: response.data.data.name || '',
          phone: response.data.data.phone || '',
          date_of_birth: response.data.data.date_of_birth || '',
          address: response.data.data.address || '',
          emergency_contact_name: response.data.data.emergency_contact_name || '',
          emergency_contact_phone: response.data.data.emergency_contact_phone || '',
          emergency_contact_relation: response.data.data.emergency_contact_relation || '',
          occupation: response.data.data.occupation || '',
          national_id: response.data.data.national_id || '',
          passport_number: response.data.data.passport_number || '',
          blood_group: response.data.data.blood_group || '',
          notes: response.data.data.notes || '',
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      const response = await api.put('/profile', formData);
      if (response.data.success) {
        setUser(response.data.data);
        setEditing(false);
        setSuccess('Profile updated successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error updating profile');
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      const response = await api.post('/profile/change-password', passwordData);
      if (response.data.success) {
        setSuccess('Password changed successfully');
        setPasswordData({
          current_password: '',
          new_password: '',
          new_password_confirmation: '',
        });
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error changing password');
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setError(null);
      const formData = new FormData();
      formData.append('profile_picture', file);

      const response = await api.post('/profile/upload-picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        const nextUrl = getImageUrl(response.data.data.profile_picture);
        setUser(prev => prev ? { ...prev, profile_picture: nextUrl } : null);
        setSuccess('Profile picture uploaded successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error uploading profile picture');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePicture = async () => {
    if (!window.confirm('Are you sure you want to delete your profile picture?')) return;

    try {
      setError(null);
      const response = await api.delete('/profile/delete-picture');
      if (response.data.success) {
        setUser(prev => prev ? { ...prev, profile_picture: null } : null);
        setSuccess('Profile picture deleted successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error deleting profile picture');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'central_committee': return 'primary';
      case 'building_committee': return 'info';
      case 'manager': return 'warning';
      case 'resident': return 'success';
      case 'visitor': return 'secondary';
      default: return 'secondary';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Super Admin';
      case 'central_committee': return 'Central Committee';
      case 'building_committee': return 'Building Committee';
      case 'manager': return 'Building Manager';
      case 'resident': return 'Resident';
      case 'visitor': return 'Visitor';
      default: return role;
    }
  };

  if (loading) {
    return (
      <div className="user-profile-loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h3>Loading Profile...</h3>
          <p>Please wait while we fetch your profile data.</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-profile-error">
        <div className="error-container">
          <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
          <h3>Error Loading Profile</h3>
          <p>{error || 'Unable to load your profile. Please try again.'}</p>
          <button className="btn btn-primary" onClick={fetchProfile}>
            <i className="fas fa-refresh mr-2"></i> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile">
      <div className="profile-header">
        <div className="profile-avatar-section">
          <div className="profile-avatar">
            {user.profile_picture ? (
              <img 
                src={getImageUrl(user.profile_picture)} 
                alt={user.name}
                className="avatar-image"
              />
            ) : (
              <div className="avatar-placeholder">
                <i className="fas fa-user"></i>
              </div>
            )}
            <div className="avatar-overlay">
              <label htmlFor="profile-picture-upload" className="upload-btn">
                <i className="fas fa-camera"></i>
              </label>
              <input
                id="profile-picture-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                disabled={uploading}
              />
              {user.profile_picture && (
                <button 
                  className="delete-btn"
                  onClick={handleDeletePicture}
                  title="Delete Profile Picture"
                >
                  <i className="fas fa-trash"></i>
                </button>
              )}
            </div>
            {uploading && (
              <div className="upload-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            )}
          </div>
        </div>
        <div className="profile-info">
          <h1>{user.name}</h1>
          <p className="profile-email">{user.email}</p>
          <div className="profile-badges">
            <span className={`badge badge-${getRoleColor(user.role)}`}>
              {getRoleLabel(user.role)}
            </span>
            <span className={`badge ${user.is_active ? 'badge-success' : 'badge-secondary'}`}>
              {user.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          {user.building && (
            <div className="profile-building">
              <i className="fas fa-building mr-2"></i>
              {user.building.name} - {user.flat_no}
            </div>
          )}
        </div>
        <div className="profile-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setEditing(!editing)}
          >
            <i className="fas fa-edit mr-2"></i>
            {editing ? 'Cancel Edit' : 'Edit Profile'}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-times-circle mr-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle mr-2"></i>
          {success}
        </div>
      )}

      <div className="profile-tabs">
        <button 
          className={`tab-btn ${activeTab === 'personal' ? 'active' : ''}`}
          onClick={() => setActiveTab('personal')}
        >
          <i className="fas fa-user mr-2"></i> Personal Info
        </button>
        <button 
          className={`tab-btn ${activeTab === 'contact' ? 'active' : ''}`}
          onClick={() => setActiveTab('contact')}
        >
          <i className="fas fa-phone mr-2"></i> Contact & Emergency
        </button>
        <button 
          className={`tab-btn ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          <i className="fas fa-id-card mr-2"></i> Documents
        </button>
        <button 
          className={`tab-btn ${activeTab === 'password' ? 'active' : ''}`}
          onClick={() => setActiveTab('password')}
        >
          <i className="fas fa-lock mr-2"></i> Change Password
        </button>
      </div>

      <div className="profile-content">
        {activeTab === 'personal' && (
          <div className="profile-section">
            <h3>Personal Information</h3>
            {editing ? (
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="date_of_birth">Date of Birth</label>
                    <input
                      type="date"
                      id="date_of_birth"
                      name="date_of_birth"
                      value={formData.date_of_birth}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="occupation">Occupation</label>
                    <input
                      type="text"
                      id="occupation"
                      name="occupation"
                      value={formData.occupation}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group full-width">
                    <label htmlFor="address">Address</label>
                    <textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows={3}
                    />
                  </div>
                  <div className="form-group full-width">
                    <label htmlFor="notes">Notes</label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={3}
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    <i className="fas fa-save mr-2"></i> Save Changes
                  </button>
                </div>
              </form>
            ) : (
              <div className="info-grid">
                <div className="info-item">
                  <label>Full Name:</label>
                  <span>{user.name}</span>
                </div>
                <div className="info-item">
                  <label>Email:</label>
                  <span>{user.email}</span>
                </div>
                <div className="info-item">
                  <label>Phone:</label>
                  <span>{user.phone || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label>Date of Birth:</label>
                  <span>{user.date_of_birth ? new Date(user.date_of_birth).toLocaleDateString() : 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label>Occupation:</label>
                  <span>{user.occupation || 'Not provided'}</span>
                </div>
                <div className="info-item full-width">
                  <label>Address:</label>
                  <span>{user.address || 'Not provided'}</span>
                </div>
                {user.notes && (
                  <div className="info-item full-width">
                    <label>Notes:</label>
                    <span>{user.notes}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="profile-section">
            <h3>Contact & Emergency Information</h3>
            {editing ? (
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="emergency_contact_name">Emergency Contact Name</label>
                    <input
                      type="text"
                      id="emergency_contact_name"
                      name="emergency_contact_name"
                      value={formData.emergency_contact_name}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="emergency_contact_phone">Emergency Contact Phone</label>
                    <input
                      type="tel"
                      id="emergency_contact_phone"
                      name="emergency_contact_phone"
                      value={formData.emergency_contact_phone}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="emergency_contact_relation">Relation</label>
                    <input
                      type="text"
                      id="emergency_contact_relation"
                      name="emergency_contact_relation"
                      value={formData.emergency_contact_relation}
                      onChange={handleInputChange}
                      placeholder="e.g., Spouse, Parent, Sibling"
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    <i className="fas fa-save mr-2"></i> Save Changes
                  </button>
                </div>
              </form>
            ) : (
              <div className="info-grid">
                <div className="info-item">
                  <label>Emergency Contact Name:</label>
                  <span>{user.emergency_contact_name || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label>Emergency Contact Phone:</label>
                  <span>{user.emergency_contact_phone || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label>Relation:</label>
                  <span>{user.emergency_contact_relation || 'Not provided'}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="profile-section">
            <h3>Document Information</h3>
            {editing ? (
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="national_id">National ID</label>
                    <input
                      type="text"
                      id="national_id"
                      name="national_id"
                      value={formData.national_id}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="passport_number">Passport Number</label>
                    <input
                      type="text"
                      id="passport_number"
                      name="passport_number"
                      value={formData.passport_number}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="blood_group">Blood Group</label>
                    <select
                      id="blood_group"
                      name="blood_group"
                      value={formData.blood_group}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Blood Group</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    <i className="fas fa-save mr-2"></i> Save Changes
                  </button>
                </div>
              </form>
            ) : (
              <div className="info-grid">
                <div className="info-item">
                  <label>National ID:</label>
                  <span>{user.national_id || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label>Passport Number:</label>
                  <span>{user.passport_number || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label>Blood Group:</label>
                  <span>{user.blood_group || 'Not provided'}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'password' && (
          <div className="profile-section">
            <h3>Change Password</h3>
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="current_password">Current Password *</label>
                  <input
                    type="password"
                    id="current_password"
                    name="current_password"
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new_password">New Password *</label>
                  <input
                    type="password"
                    id="new_password"
                    name="new_password"
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    required
                    minLength={8}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new_password_confirmation">Confirm New Password *</label>
                  <input
                    type="password"
                    id="new_password_confirmation"
                    name="new_password_confirmation"
                    value={passwordData.new_password_confirmation}
                    onChange={handlePasswordChange}
                    required
                    minLength={8}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-warning">
                  <i className="fas fa-key mr-2"></i> Change Password
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {user.ownerships && user.ownerships.length > 0 && (
        <div className="profile-section">
          <h3>Property Ownership</h3>
          <div className="ownerships-grid">
            {user.ownerships.map(ownership => (
              <div key={ownership.id} className="ownership-card">
                <div className="ownership-header">
                  <h4>{ownership.flat_number}</h4>
                  <span className={`badge badge-${getRoleColor(ownership.ownership_type)}`}>
                    {ownership.ownership_type.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="ownership-details">
                  <div className="ownership-info">
                    <span className="building-name">{ownership.building.name}</span>
                    <span className="flat-type">{ownership.flat.flat_type}</span>
                    <span className="ownership-percentage">{ownership.ownership_percentage}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;

